<template>
  <PrimeDialog
    :header="$t('sharelink.linkToShare')"
    visible
    modal
    class="popup popup--share-link"
    @keyup.esc="closePopup(false)"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        outlined
        text
        severity="secondary"
        autofocus="false"
        @click="closePopup(false)"
      />
    </template>
    <p class="share-link-description">
      {{ $t("sharelink.shareLinkOf", { fileName: node.name }) }}
    </p>
    <PrimeInputGroup>
      <PrimeInputText
        v-model="link"
        :placeholder="$t('sharelink.placeholder')"
        disabled
        class="share-link-input"
      />
      <PrimeInputGroupAddon v-if="!shareId">
        <PrimeButton severity="success" @click="createSharedLink">
          <i class="ri-add-line" aria-hidden="true" />&nbsp;{{
            $t(`sharelink.createLinkTooltip`)
          }}
        </PrimeButton>
      </PrimeInputGroupAddon>
      <PrimeInputGroupAddon v-else class="share-link-actions">
        <PrimeButton severity="warning" @click="copyLinkClipboard">
          <i class="ri-link" aria-hidden="true" />&nbsp;{{
            $t(`sharelink.copyLinkTooltip`)
          }}
        </PrimeButton>
        <PrimeButton
          v-tooltip="$t(`sharelink.deleteLinkTooltip`)"
          :aria-label="$t('sharelink.deleteLinkTooltip')"
          icon="ri-delete-bin-line"
          severity="danger"
          @click="deleteSharedLink"
        />
      </PrimeInputGroupAddon>
    </PrimeInputGroup>
    <div v-if="shareId" class="expiration-section">
      <div class="expiration-checkbox">
        <PrimeCheckbox
          v-model="hasExpirationDate"
          v-tooltip="$t(`sharelink.addExpirationDateLinkTooltip`)"
          input-id="expirationDate"
          binary
          @change="dateChecked"
        />
        <label for="expirationDate">{{ $t("sharelink.expirationDate") }}</label>
      </div>
      <PrimeInputGroup v-if="hasExpirationDate" class="expiration-input">
        <PrimeDatePicker
          v-model="date"
          :min-date="new Date()"
          @date-select="updateLinkDate"
        />
        <PrimeInputGroupAddon>
          <PrimeButton
            v-tooltip="$t(`sharelink.deleteExpirationDateLinkTooltip`)"
            :aria-label="$t('sharelink.deleteExpirationDateLinkTooltip')"
            icon="ri-delete-bin-line"
            severity="danger"
            @click="deleteLinkDate"
          />
        </PrimeInputGroupAddon>
      </PrimeInputGroup>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('close')"
        severity="secondary"
        @click="closePopup"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import { useSelectionStore } from "@/stores/selection.js";
import { useConfigStore } from "@/stores/config.js";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import Checkbox from "primevue/checkbox";
import DatePicker from "primevue/datepicker";
import Dialog from "primevue/dialog";

export default {
  name: "ShareLinkPopup",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeInputText: InputText,
    PrimeInputGroup: InputGroup,
    PrimeInputGroupAddon: InputGroupAddon,
    PrimeCheckbox: Checkbox,
    PrimeDatePicker: DatePicker,
  },
  directives: {
    tooltip: Tooltip,
  },
  emits: ["close", "reload"],
  data() {
    return {
      link: null,
      shareId: null,
      date: null,
      hasExpirationDate: false,
      node: null,
    };
  },
  watch: {
    shareId(newValue) {
      if (newValue !== null) {
        const config = useConfigStore();
        this.link = `${config.PREVIEW_URL}${newValue}`;
      } else {
        this.link = null;
      }
    },
  },
  mounted() {
    this.node = useSelectionStore().files[0];
    this.shareId = this.node.properties["qshare:sharedId"] ?? null;
    this.hasExpirationDate = "qshare:expiryDate" in this.node.properties;
    this.date = this.hasExpirationDate
      ? new Date(this.node.properties["qshare:expiryDate"])
      : null;
  },

  methods: {
    closePopup(reload) {
      this.$emit("close", reload);
    },
    dateChecked() {
      if (!this.hasExpirationDate) {
        this.deleteLinkDate();
      }
    },
    copyLinkClipboard() {
      navigator.clipboard.writeText(this.link).then(() => {
        this.$toast.add({
          severity: "success",
          summary: this.$t("sharelink.copyLinkToastSummary"),
          detail: this.$t("sharelink.copyLinkToastDetail"),
          life: 3000,
        });
      });
    },
    deleteLinkDate() {
      this.date = null;
      this.hasExpirationDate = false;
      this.updateLinkDate();
      this.$emit("reload");
    },
    updateLinkDate() {
      const opts = {
        properties: {
          "qshare:expiryDate": this.date ? this.date.toISOString() : null,
        },
      };
      alfrescoNodeService
        .updateNode(this.node.id, opts)
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("sharelink.updateLinkDateSummary"),
            detail: this.$t("sharelink.updateLinkDateDetail"),
            life: 3000,
          });
          this.$emit("reload");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("sharelink.updateLinkDateErrorSummary"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    deleteSharedLink() {
      alfrescoNodeService
        .deleteSharedLink(this.shareId)
        .then(() => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("sharelink.deleteLinkToastSummary"),
            detail: this.$t("sharelink.deleteLinkToastDetail"),
            life: 3000,
          });
          this.$emit("reload");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("deleteLinkErrorSummary"),
            detail: err.message,
            life: 3000,
          });
        });
      this.shareId = null;
      this.link = null;
      this.date = null;
      this.hasExpirationDate = false;
    },
    createSharedLink() {
      alfrescoNodeService
        .createSharedLink(this.node)
        .then((data) => {
          this.shareId = data.entry.id;
          this.$toast.add({
            severity: "success",
            summary: this.$t("sharelink.createLinkToastSummary"),
            detail: this.$t("sharelink.createLinkToastDetail"),
            life: 3000,
          });

          // Force PDF Rendition
          alfrescoFileService.createRendition(this.node.id, "pdf");
          this.$emit("reload");
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("sharelink.createLinkErrorSummary"),
            detail: err.message,
            life: 3000,
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.share-link-description {
  margin: 0 0 1rem 0;
  color: var(--p-text-muted-color);
}

.share-link-input {
  flex: 1;
}

.share-link-actions {
  display: flex;
  gap: 0.25rem;
  padding: 0;
  background: transparent;
  border: none;
}

.expiration-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-200);
}

.expiration-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  label {
    cursor: pointer;
    font-weight: 500;
    color: var(--p-text-color);
  }
}

.expiration-input {
  margin-top: 0.5rem;
}
</style>
